import { CodeEditor } from '@kodprep/platform-ui/organisms';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, ExternalLink, X } from 'lucide-react';
import { useState } from 'react';

import { KIND_LABEL } from './workVocabulary';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  trainerApi,
  type ArtifactAttempt,
  type ArtifactCode,
  type ArtifactHandin,
  type ArtifactQuizAnswer,
  type WorkArtifact,
} from '@/lib/api/trainer';
import { cn } from '@/lib/cn';

/**
 * The work itself.
 *
 * Every other screen in this feature describes student work — finished or not,
 * typed or pasted, like someone else's or not. None of that is worth much to a
 * trainer who cannot open the thing and read it, and a signal nobody can check
 * is a signal nobody should be repeating out loud. This is the checking.
 */

/** Verdicts as a trainer would say them, not as the judge stores them. */
const VERDICT_LABEL: Record<string, string> = {
  AC: 'Accepted',
  WA: 'Wrong answer',
  TLE: 'Time limit exceeded',
  MLE: 'Memory limit exceeded',
  RE: 'Runtime error',
  CE: 'Compile error',
  PENDING: 'Pending',
};

function verdictTone(verdict: string): string {
  return verdict === 'AC'
    ? 'border-green-200 bg-green-100 text-green-700'
    : 'border-amber-200 bg-amber-50 text-amber-700';
}

export function WorkArtifactSheet({
  batchId,
  assessmentId,
  student,
  onClose,
  onSeeEveryone,
  batchStudentCount,
}: {
  batchId: string;
  assessmentId: string;
  student: { id: string; name: string } | null;
  onClose: () => void;
  /**
   * The way out to the same piece of work with the whole batch against it.
   *
   * Optional because this sheet already opens *from* that screen, where the
   * link would point at itself. It matters when the sheet is reached from a
   * person instead — one student's paste ratio means little until you know
   * whether twenty classmates have the same one.
   */
  onSeeEveryone?: () => void;
  /** Only for wording the link; omitted it says "everyone" instead. */
  batchStudentCount?: number;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trainer', 'artifact', batchId, assessmentId, student?.id],
    queryFn: async () =>
      (await trainerApi.getWorkArtifact(batchId, assessmentId, student!.id)).data,
    enabled: !!student,
  });

  return (
    <Sheet open={!!student} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="pr-8 text-base">
            {student?.name}
            {data && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {KIND_LABEL[data.kind]} · {data.item_title}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-64 w-full" />
            </div>
          )}
          {isError && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              This work could not be loaded.
            </p>
          )}
          {data && <ArtifactBody artifact={data} batchId={batchId} />}
        </div>

        {onSeeEveryone && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t px-5 py-3 text-xs text-muted-foreground">
            <span>One person&apos;s record only.</span>
            <Button
              variant="link"
              size="sm"
              onClick={onSeeEveryone}
              className="h-auto gap-1 p-0 text-xs"
            >
              See how {batchStudentCount ? `all ${batchStudentCount} students` : 'everyone'} did on
              this
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ArtifactBody({ artifact, batchId }: { artifact: WorkArtifact; batchId: string }) {
  if (artifact.empty) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {/* Said plainly. "No submission" is a finding in its own right. */}
        This student has not submitted anything here yet.
      </p>
    );
  }

  if (artifact.kind === 'quiz' && artifact.quiz) {
    return <QuizAnswers answers={artifact.quiz} />;
  }
  if (artifact.code) {
    return (
      <PracticeCode code={artifact.code} attempts={artifact.attempts ?? []} batchId={batchId} />
    );
  }
  if (artifact.handin) {
    return <Handin handin={artifact.handin} />;
  }
  return null;
}

/**
 * The submitted solution, plus the attempts that led to it.
 *
 * The history is the part a paste ratio cannot give you: six wrong answers then
 * an accept is a student working, and one accept on the first try after four
 * seconds is a different story, told by the same "Done" badge.
 */
function PracticeCode({
  code,
  attempts,
  batchId,
}: {
  code: ArtifactCode;
  attempts: ArtifactAttempt[];
  batchId: string;
}) {
  const [openId, setOpenId] = useState(code.submission_id);

  // Only fetched when the trainer opens an earlier attempt; the submission they
  // stand behind arrived with the artifact.
  const { data: opened } = useQuery({
    queryKey: ['trainer', 'submission-code', batchId, openId],
    queryFn: async () => (await trainerApi.getSubmissionCode(batchId, openId)).data,
    enabled: openId !== code.submission_id,
  });
  const shown = openId === code.submission_id ? code : opened;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <Badge variant="outline" className={cn('py-px', verdictTone(shown?.verdict ?? ''))}>
          {VERDICT_LABEL[shown?.verdict ?? ''] ?? shown?.verdict}
        </Badge>
        <span className="font-medium text-foreground">{shown?.language}</span>
        {shown?.passed_cases != null && shown?.total_cases != null && (
          <span className="tabular-nums">
            {shown.passed_cases}/{shown.total_cases} tests
          </span>
        )}
        {shown?.runtime_ms != null && <span className="tabular-nums">{shown.runtime_ms} ms</span>}
        {shown?.memory_kb != null && (
          <span className="tabular-nums">{Math.round(shown.memory_kb / 1024)} MB</span>
        )}
        <span>{formatWhenExact(shown?.submitted_at)}</span>
      </div>

      {shown?.error_msg && (
        <Card className="border-amber-200 bg-amber-50/60 p-3 shadow-none">
          <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-amber-900">
            {shown.error_msg}
          </pre>
        </Card>
      )}

      <Card className="overflow-hidden p-0 shadow-none">
        <CodeEditor
          value={shown?.code ?? ''}
          language={shown?.language ?? 'plaintext'}
          readOnly
          height="420px"
        />
      </Card>

      {/* One attempt is not a history worth a heading. */}
      {attempts.length > 1 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold">All {attempts.length} attempts</p>
          <ul className="space-y-1">
            {attempts.map((a) => (
              <li key={a.submission_id}>
                <button
                  type="button"
                  onClick={() => setOpenId(a.submission_id)}
                  aria-pressed={openId === a.submission_id}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-accent/50',
                    openId === a.submission_id ? 'border-primary/40 bg-accent/40' : 'border-border',
                  )}
                >
                  <Badge variant="outline" className={cn('py-px', verdictTone(a.verdict))}>
                    {VERDICT_LABEL[a.verdict] ?? a.verdict}
                  </Badge>
                  {a.passed_cases != null && a.total_cases != null && (
                    <span className="tabular-nums text-muted-foreground">
                      {a.passed_cases}/{a.total_cases}
                    </span>
                  )}
                  <span className="ml-auto tabular-nums text-muted-foreground">
                    {formatWhenExact(a.created_at)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Every question, with what they picked beside what was right.
 *
 * The score is already on the roster; the reason to open a quiz is to find out
 * which ones the batch missed, which is what tells a trainer what to reteach.
 */
function QuizAnswers({ answers }: { answers: ArtifactQuizAnswer[] }) {
  const right = answers.filter((a) => a.chosen_option === a.correct_option).length;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">{right}</span> of {answers.length} correct
      </p>

      <ol className="space-y-2">
        {answers.map((a, i) => {
          const answered = a.chosen_option != null;
          const correct = a.chosen_option === a.correct_option;
          return (
            <li key={a.question_id}>
              <Card className="space-y-2 p-3 shadow-none">
                <div className="flex items-start gap-2">
                  <span className="text-xs font-semibold tabular-nums text-muted-foreground">
                    {i + 1}.
                  </span>
                  <p className="flex-1 text-sm font-medium">{a.question_text}</p>
                  {!answered ? (
                    <Badge variant="outline" className="py-px text-[10.5px] text-muted-foreground">
                      Not answered
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className={cn(
                        'py-px text-[10.5px]',
                        correct
                          ? 'border-green-200 bg-green-100 text-green-700'
                          : 'border-amber-200 bg-amber-50 text-amber-700',
                      )}
                    >
                      {correct ? 'Correct' : 'Wrong'}
                    </Badge>
                  )}
                </div>

                <ul className="space-y-0.5 pl-6">
                  {a.options.map((opt, oi) => {
                    const isChosen = a.chosen_option === oi;
                    const isRight = a.correct_option === oi;
                    return (
                      <li
                        key={oi}
                        className={cn(
                          'flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs',
                          isRight && 'bg-green-50 font-medium text-green-800',
                          isChosen && !isRight && 'bg-amber-50 font-medium text-amber-800',
                        )}
                      >
                        {isRight && <Check className="h-3 w-3 flex-none" aria-hidden="true" />}
                        {isChosen && !isRight && (
                          <X className="h-3 w-3 flex-none" aria-hidden="true" />
                        )}
                        <span className={cn(!isRight && !isChosen && 'pl-[18px]')}>{opt}</span>
                        {isChosen && (
                          <span className="text-[10.5px] font-normal opacity-70">
                            — they picked this
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Card>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * An assignment or project. The work lives somewhere else — GitHub, Drive — so
 * the most this can do is take the trainer there and show what was said about
 * it last time.
 */
function Handin({ handin }: { handin: ArtifactHandin }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {handin.score != null && (
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {handin.score}
            {handin.max_score != null && `/${handin.max_score}`}
          </span>
        )}
        {handin.submitted_at && <span>Submitted {formatWhenExact(handin.submitted_at)}</span>}
        {handin.graded_at ? (
          <span>Graded {formatWhenExact(handin.graded_at)}</span>
        ) : (
          <Badge variant="outline" className="border-sky-200 bg-sky-50 py-px text-sky-700">
            Not graded yet
          </Badge>
        )}
      </div>

      {handin.submission_url ? (
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <a href={handin.submission_url} target="_blank" rel="noreferrer noopener">
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            Open what they handed in
          </a>
        </Button>
      ) : (
        <p className="text-sm text-muted-foreground">No link was submitted.</p>
      )}

      {handin.feedback && (
        <Card className="p-3 shadow-none">
          <p className="mb-1 text-xs font-semibold">Feedback given</p>
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{handin.feedback}</p>
        </Card>
      )}
    </div>
  );
}

/**
 * Date and time here, unlike the lists elsewhere: when reading one submission
 * the minute is exactly what a trainer wants — it is how "four seconds after
 * they opened it" becomes visible.
 */
function formatWhenExact(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export { VERDICT_LABEL, verdictTone, formatWhenExact };
